// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorProductsPage
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Grid, List } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { ProductCard } from '@/features/vendor/components/ProductCard';
import { useVendorProducts, useDeleteProduct, useUpdateProductStatus } from '@/features/vendor/hooks/useVendorProducts';
import { useVendorStore } from '@/features/vendor/stores/vendorStore';

export function VendorProductsPage() {
  const { productFilters, setProductFilters } = useVendorStore();
  const { data, isLoading, fetchNextPage, hasNextPage } = useVendorProducts(productFilters);
  const deleteProduct = useDeleteProduct();
  const toggleStatus = useUpdateProductStatus();

  const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = React.useState('');

  const products = data?.pages.flatMap((page) => page.items) || [];

  const handleDelete = async (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      await deleteProduct.mutateAsync(productId);
    }
  };

  const handleToggleStatus = async (productId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'draft' : 'active';
    await toggleStatus.mutateAsync({ productId, status: newStatus });
  };

  return (
    <div className="min-h-screen">
      <VendorHeader
        title="Products"
        subtitle={`${products.length} products in your catalog`}
        actions={
          <Link
            to="/vendor/products/new"
            className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white font-medium rounded-xl hover:bg-terracotta-light transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </Link>
        }
      />

      <div className="p-6">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                />
              </div>
            </div>

            <select
              value={productFilters.status || ''}
              onChange={(e) => setProductFilters({ status: e.target.value || undefined })}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>

            <select
              value={productFilters.categoryId || ''}
              onChange={(e) => setProductFilters({ categoryId: e.target.value || undefined })}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
            >
              <option value="">All Categories</option>
            </select>

            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-terracotta text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-terracotta text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid/List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="h-40 bg-gray-200 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-navy mb-2">No products yet</h3>
            <p className="text-warm-gray mb-4">Add your first product to start selling</p>
            <Link
              to="/vendor/products/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white font-medium rounded-xl hover:bg-terracotta-light"
            >
              <Plus className="w-5 h-5" />
              Add Product
            </Link>
          </div>
        ) : (
          <>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => {}}
                  onDelete={() => handleDelete(product.id)}
                  onToggleStatus={() => handleToggleStatus(product.id, product.status)}
                />
              ))}
            </div>

            {hasNextPage && (
              <div className="text-center mt-8">
                <button
                  onClick={() => fetchNextPage()}
                  className="px-6 py-2 bg-white border border-gray-200 text-navy font-medium rounded-xl hover:bg-cream-dark transition-colors"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default VendorProductsPage;

